import { FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { getSourcesForCollection } from "../lib/documentSearch/documentSearchClient";
import type { Collection, SourceDocument } from "../lib/documentSearch/types";

type SourceSetBrowserProps = {
  collection: Collection;
  onSourceSelect: (document: SourceDocument) => void;
};

export function SourceSetBrowser({
  collection,
  onSourceSelect,
}: SourceSetBrowserProps) {
  const [sources, setSources] = useState<SourceDocument[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getSourcesForCollection(collection.id)
      .then((nextSources) => {
        if (mounted) {
          setSources(nextSources);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [collection.id]);

  return (
    <section className="source-set-browser" aria-label={`${collection.name} sources`}>
      <div className="source-set-browser__header">
        <div>
          <p>Sources</p>
          <h3>{collection.name}</h3>
        </div>
        <span>{sources.length}</span>
      </div>

      {loading ? (
        <p className="source-set-browser__empty">Loading sources...</p>
      ) : sources.length > 0 ? (
        <div className="source-set-browser__list">
          {sources.map((source) => (
            <button
              key={source.id}
              type="button"
              className="source-set-source"
              onClick={() => onSourceSelect(source)}
            >
              <FileText size={16} aria-hidden="true" />
              <span>
                <strong>{source.title}</strong>
                <small>
                  {source.type}
                  {source.year > 0 ? ` | ${source.year}` : ""}
                </small>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="source-set-browser__empty">No sources in this set.</p>
      )}
    </section>
  );
}
